import React, { useState, useEffect } from 'react';
import {
  FolderTree, Plus, Edit3, Trash2, FileText,
  AlertCircle, Check, X, Folder
} from 'lucide-react';
import { adminApi } from '../../lib/adminApi';
import { Button } from '../../components/ui/button';
import BlogNavTabs from './BlogNavTabs';
import './AdminBlog.css';

export default function AdminBlogCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete State
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCategories = () => {
    setLoading(true);
    setError('');
    adminApi.get('/admin/blog-categories')
      .then(res => setCategories(res.data || []))
      .catch(err => setError(err.response?.data?.detail || "Failed to load categories."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const openCreateModal = () => {
    setEditingCategory(null);
    setName('');
    setSlug('');
    setDescription('');
    setModalOpen(true);
  };

  const openEditModal = (cat) => {
    setEditingCategory(cat);
    setName(cat.name || '');
    setSlug(cat.slug || '');
    setDescription(cat.description || '');
    setModalOpen(true);
  };

  const handleNameChange = (e) => {
    const val = e.target.value;
    setName(val);
    if (!editingCategory) {
      setSlug(val.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-'));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    setSaving(true);
    setError('');
    const payload = {
      name: name.trim(),
      slug: slug.trim(),
      description: description.trim()
    };

    try {
      if (editingCategory) {
        await adminApi.put(`/admin/blog-categories/${editingCategory.id}`, payload);
        setSuccess(`Category "${name}" updated successfully.`);
      } else {
        await adminApi.post('/admin/blog-categories', payload);
        setSuccess(`Category "${name}" created successfully.`);
      }
      setModalOpen(false);
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save category.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (catId) => {
    setDeleting(true);
    try {
      await adminApi.delete(`/admin/blog-categories/${catId}`);
      setSuccess("Category removed.");
      setDeleteConfirmId(null);
      fetchCategories();
    } catch (err) {
      setError("Failed to delete category.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <main className="space-y-6">
      <BlogNavTabs
        title="Categories"
        description="Organize blog posts into distinct categories (e.g. News, Guides, Case Studies)."
        showCreateButton={false}
      />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 border border-slate-200 rounded-xl shadow-sm">
        <div>
          <h3 className="text-base font-bold text-slate-900">Blog Categories</h3>
          <p className="text-xs text-slate-500">
            Categories define your article URLs (e.g. /blogs/news/...) and structure website navigation.
          </p>
        </div>
        <Button
          onClick={openCreateModal}
          className="admin-primary flex items-center gap-2 text-xs font-semibold"
        >
          <Plus size={15} />
          <span>Add New Category</span>
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <div role="alert" className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2 text-sm">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div role="status" className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg flex items-center gap-2 text-sm">
          <Check size={16} />
          <span>{success}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white border rounded-xl p-8 text-center text-slate-500">
          <div className="admin-spinner mb-3" />
          <p className="text-sm">Loading categories...</p>
        </div>
      )}

      {/* Category Cards */}
      {!loading && categories.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((cat) => (
            <div
              key={cat.id}
              className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:border-slate-300 transition-all flex flex-col justify-between"
            >
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                    <Folder size={18} />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">{cat.name}</h4>
                    <span className="text-[11px] font-mono text-slate-400">/blogs/{cat.slug}</span>
                  </div>
                </div>

                {cat.description ? (
                  <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed pt-1">
                    {cat.description}
                  </p>
                ) : (
                  <p className="text-xs text-slate-400 italic pt-1">No description provided.</p>
                )}
              </div>

              <div className="pt-4 mt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span className="flex items-center gap-1 font-medium text-slate-700">
                  <FileText size={13} className="text-amber-600" />
                  <span>{cat.post_count || 0} posts</span>
                </span>

                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal(cat)}
                    className="h-7 px-2 text-xs text-slate-600 hover:text-amber-600 hover:bg-amber-50"
                  >
                    <Edit3 size={13} className="mr-1" /> Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteConfirmId(cat.id)}
                    className="h-7 px-2 text-xs text-slate-400 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Modal: Add / Edit Category ── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <form onSubmit={handleSave} className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b pb-3">
              <h4 className="font-bold text-slate-900 text-base">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h4>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Category Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. AI News & Trends"
                  value={name}
                  onChange={handleNameChange}
                  className="w-full text-sm border rounded-lg p-2.5 focus:border-amber-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">URL Slug</label>
                <input
                  type="text"
                  placeholder="ai-news-trends"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^\w-]/g, ''))}
                  className="w-full text-sm font-mono border rounded-lg p-2.5 focus:border-amber-500 focus:outline-none"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Prefix: /blogs/{slug || 'category-name'}/
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Description (Optional)</label>
                <textarea
                  rows={3}
                  placeholder="Brief summary of articles in this category..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full text-sm border rounded-lg p-2.5 focus:border-amber-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={saving || !name.trim()}
                className="admin-primary"
              >
                {saving ? 'Saving...' : (editingCategory ? 'Save Changes' : 'Create Category')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* ── Modal: Delete Confirmation ── */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 space-y-4">
            <h4 className="font-bold text-slate-900 text-base">Delete category?</h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              Are you sure? Existing articles under this category will become &ldquo;Uncategorized&rdquo;.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteConfirmId(null)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={deleting}
                onClick={() => handleDelete(deleteConfirmId)}
                className="admin-danger"
              >
                {deleting ? 'Deleting...' : 'Delete Category'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
